import { useState } from "react";
import { useApi } from "../../lib/useApi";
import { navegar } from "../../lib/ruta";
import { FRASE_AUDIO } from "../../lib/textos";
import { fechaCorta } from "../../lib/fecha-ui";
import { Card, Button, Chip, SectionLabel, IconOido, cn } from "../../components/ui";
import type { SesionActiva } from "../../lib/tipos";

// Overview de Ajustes (IA nueva): una pantalla con tarjetas que resumen y enlazan a las páginas de
// detalle. SOLO lee (useApi); toda mutación vive en las páginas de detalle. Los conteos/estado sin
// backend NO se inventan: se rotulan sobrio.
//
// Tramo 03 (Kevin, 27-sep-2026): el Catálogo salió a su propia sección del menú (un objeto, un hogar)
// y Ajustes queda en 3 bloques rotulados. Cada botón dice «Abrir» + el nombre de la pantalla que abre.

type Usuario = { id: string; nombre: string; email: string; rol: string; sucursal_id: string | null; activo: number };
type Sucursal = { id: string; nombre: string; direccion: string | null; activa: number; hora_apertura: string | null; hora_cierre: string | null };
type Dispositivo = { id: string; nombre: string; activo: number; sucursal_id: string; created_at: string };
type CalidadDia = { fecha: string; senales: number; senales_sin_match: number };
type Correccion = { id: string; texto_norm: string; producto_nombre: string | null };

const ROL_LABEL: Record<string, string> = {
  super_admin: "Dueño",
  admin_sucursal: "Encargado",
  operador: "Vendedor",
  lector_reportes: "Lector",
};

export function Ajustes({ sesion }: { sesion: SesionActiva }) {
  const esSuper = sesion.usuario.rol === "super_admin";

  // Tenant-wide (funcionan para dueño y encargado por igual).
  // A4: `/sugerencias/reglas` devuelve SOLO las activas — que es justo lo que se quiere contar acá.
  const reglas = useApi<{ reglas: unknown[] }>("/sugerencias/reglas");
  const usuarios = useApi<{ usuarios: Usuario[] }>("/usuarios");
  const sucursales = useApi<{ sucursales: Sucursal[] }>("/sucursales");
  const correcciones = useApi<{ correcciones: Correccion[] }>("/audio/correcciones");

  // Dispositivos y el oído son POR BOTICA en el server (sucursalVerificada): el encargado usa la suya;
  // el dueño elige una (default = la primera). Los demás datos siguen siendo de toda la cadena.
  const [sucSel, setSucSel] = useState("");
  const primeraSuc = sucursales.data?.sucursales[0]?.id ?? "";
  const sucScope = esSuper ? (sucSel || primeraSuc) : (sesion.usuario.sucursalId ?? "");
  const listoScope = !esSuper || !!sucScope;
  const qSuc = esSuper && sucScope ? `?sucursal_id=${sucScope}` : "";
  const dispositivos = useApi<{ dispositivos: Dispositivo[] }>(listoScope ? `/dispositivos${qSuc}` : null, [sucScope]);
  const audio = useApi<{ dias: CalidadDia[] }>(listoScope ? `/audio/calidad${qSuc ? `${qSuc}&` : "?"}dias=7` : null, [sucScope]);

  const sucNombre = (id: string | null): string | null => sucursales.data?.sucursales.find((s) => s.id === id)?.nombre ?? null;
  const rolAlcance = (u: Usuario): string =>
    u.rol === "super_admin" ? "Dueño · toda la cadena" : `${ROL_LABEL[u.rol] ?? u.rol}${sucNombre(u.sucursal_id) ? ` · ${sucNombre(u.sucursal_id)}` : ""}`;

  // El oído: % de señales útiles (reconocidas / total) derivado de los 7 días.
  const dias = audio.data?.dias ?? [];
  const senales = dias.reduce((a, d) => a + d.senales, 0);
  const sinMatch = dias.reduce((a, d) => a + d.senales_sin_match, 0);
  const reconocidas = Math.max(0, senales - sinMatch);
  const pct = senales > 0 ? Math.round((reconocidas / senales) * 100) : null;

  const us = usuarios.data?.usuarios ?? [];
  const usVisibles = us.slice(0, 6);
  const usResto = us.length - usVisibles.length;
  const sucs = sucursales.data?.sucursales ?? [];
  const disp = dispositivos.data?.dispositivos ?? [];
  const dicc = correcciones.data?.correcciones ?? [];

  return (
    <div className="flex flex-col gap-3.5">
      {esSuper && sucs.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-ink-2">Dispositivos y el oído de:</span>
          <select
            value={sucScope}
            onChange={(e) => setSucSel(e.target.value)}
            className="rounded-[9px] border border-line-input bg-field px-3 py-1.5 text-[13px] text-ink outline-none"
          >
            {sucs.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-2 items-start gap-3.5">
        {/* Mapa del sistema (orientación) */}
        <Card className="col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <SectionLabel>Mapa del sistema</SectionLabel>
              <p className="mt-1 text-[12.5px] leading-relaxed text-ink-2">
                ¿No encuentras una pantalla? El mapa lista todo el sistema: qué hay en cada sección, para qué sirve y quién lo ve.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navegar("mapa")}>Abrir el mapa</Button>
          </div>
        </Card>

        <h2 className="col-span-2 mt-2 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-2">Tu equipo y tus boticas</h2>
        {/* Usuarios y roles */}
        <Card>
          <SectionLabel>Usuarios y roles</SectionLabel>
          {usuarios.cargando ? (
            <p className="mt-2 text-[12.5px] text-ink-3">Cargando usuarios…</p>
          ) : usuarios.error ? (
            <p className="mt-2 text-[12.5px] text-accent-ink">{usuarios.error}</p>
          ) : (
            <ul className="mt-1.5 flex flex-col">
              {usVisibles.map((u) => (
                <li key={u.id} className="flex items-center justify-between gap-2 border-b border-line-row py-2 last:border-0">
                  <div className="min-w-0">
                    <p className={cn("truncate text-[13px] font-semibold", u.activo !== 1 ? "text-ink-3 line-through" : "text-ink")}>{u.nombre}</p>
                    <p className="truncate text-[12px] text-ink-2">{rolAlcance(u)}</p>
                  </div>
                  {u.activo !== 1 && <Chip variant="neutral">inactivo</Chip>}
                </li>
              ))}
            </ul>
          )}
          {usResto > 0 && <p className="mt-1.5 text-[12px] text-ink-3">y {usResto} usuario{usResto === 1 ? "" : "s"} más</p>}
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => navegar("usuarios")}>Abrir usuarios</Button>
          </div>
        </Card>

        {/* Boticas */}
        <Card>
          <SectionLabel>Boticas</SectionLabel>
          {sucursales.cargando ? (
            <p className="mt-2 text-[12.5px] text-ink-3">Cargando boticas…</p>
          ) : sucursales.error ? (
            <p className="mt-2 text-[12.5px] text-accent-ink">{sucursales.error}</p>
          ) : (
            <ul className="mt-1.5 flex flex-col">
              {sucs.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-2 border-b border-line-row py-2 last:border-0">
                  <span className={cn("truncate text-[13px] font-semibold", s.activa !== 1 ? "text-ink-3" : "text-ink")}>{s.nombre}</span>
                  <Chip variant={s.activa === 1 ? "ok" : "neutral"}>{s.activa === 1 ? "Activa" : "Inactiva"}</Chip>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[12px] leading-relaxed text-ink-2">
            Una botica aparece abierta desde su primera venta o apertura del cajón del día.
          </p>
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => navegar("sucursales")}>Abrir boticas</Button>
          </div>
        </Card>

        {esSuper && <Bitacora />}

        <h2 className="col-span-2 mt-2 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-2">El mostrador</h2>
        {/* Sugerencias del mostrador (A4) */}
        <Card className="col-span-2">
          <SectionLabel>Sugerencias del mostrador</SectionLabel>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-[24px] font-bold tracking-[-0.01em] tabular-nums text-ink">
              {reglas.cargando ? "…" : reglas.error ? "—" : (reglas.data?.reglas.length ?? 0)}
            </span>
            <span className="text-[13px] font-medium text-ink-2">reglas encendidas</span>
          </div>
          <p className="mt-2 text-[12.5px] leading-relaxed text-ink-2">
            Cuando se agrega un producto que calza con una regla, el mostrador muestra una sola tarjeta con el consejo.
            La pantalla lleva la cuenta de cuántas se aceptan, para apagar las que no sirven.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navegar("sugerencias")}>Abrir sugerencias</Button>
          </div>
        </Card>

        <h2 className="col-span-2 mt-2 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-2">Equipos y el oído · piloto</h2>
        {/* Grabadores (una pantalla, un nombre: «Grabadores», decisión de Kevin del 27-sep-2026) */}
        <Card>
          <SectionLabel>Grabadores</SectionLabel>
          {!listoScope ? (
            <p className="mt-2 text-[12.5px] text-ink-3">Elige una botica arriba para ver sus grabadores.</p>
          ) : dispositivos.cargando ? (
            <p className="mt-2 text-[12.5px] text-ink-3">Cargando grabadores…</p>
          ) : dispositivos.error ? (
            <p className="mt-2 text-[12.5px] text-accent-ink">{dispositivos.error}</p>
          ) : disp.length === 0 ? (
            <p className="mt-2 text-[12.5px] text-ink-3">Aún no hay grabadores en esta botica.</p>
          ) : (
            <ul className="mt-1.5 flex flex-col">
              {disp.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2 border-b border-line-row py-2 last:border-0">
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-semibold text-ink">{d.nombre}</p>
                    <p className="truncate text-[12px] text-ink-2">{sucNombre(d.sucursal_id) ?? "—"}</p>
                  </div>
                  <Chip variant="neutral">próximamente</Chip>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[12px] leading-relaxed text-ink-3">
            El estado en vivo (en línea, batería, apagado) llegará con la telemetría de los equipos.
          </p>
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => navegar("grabadores")}>Abrir grabadores</Button>
          </div>
        </Card>

        {/* El oído — calidad y diccionario */}
        <Card>
          <div className="flex items-center gap-2">
            <IconOido size={14} className="text-accent" />
            <SectionLabel>El oído — calidad y diccionario</SectionLabel>
          </div>
          <div className="mt-3 flex flex-col gap-4">
            {/* Izquierda: calidad de la semana */}
            <div>
              <p className="text-[22px] font-bold tracking-[-0.01em] tabular-nums text-ink">
                {!listoScope ? "—" : audio.cargando ? "…" : pct === null ? "Sin señales esta semana" : `${pct}% de señales útiles esta semana`}
              </p>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-neutral-soft">
                <div className="h-full rounded-full bg-accent" style={{ width: `${pct ?? 0}%` }} />
              </div>
              <p className="mt-2.5 text-[12.5px] leading-relaxed text-ink-2">
                {reconocidas} frase{reconocidas === 1 ? "" : "s"} reconocida{reconocidas === 1 ? "" : "s"} de {senales} señal{senales === 1 ? "" : "es"} esta semana. Cada vez que confirmas o descartas una señal, el oído aprende.
              </p>
              <p className="mt-2 text-[12px] leading-relaxed text-ink-3">
                {FRASE_AUDIO}
              </p>
            </div>
            {/* Derecha: diccionario */}
            <div>
              <p className="text-[13px] font-semibold text-ink">
                Diccionario · {correcciones.cargando ? "…" : dicc.length >= 200 ? "200+" : dicc.length} término{dicc.length === 1 ? "" : "s"} aprendido{dicc.length === 1 ? "" : "s"}
              </p>
              {dicc.length === 0 && !correcciones.cargando ? (
                <p className="mt-2 text-[12px] text-ink-3">Aún no hay términos. Se aprenden al asignar productos a las señales sin reconocer.</p>
              ) : (
                <ul className="mt-2 flex flex-col gap-1.5">
                  {dicc.slice(0, 3).map((x) => (
                    <li key={x.id} className="rounded-[8px] border border-line-inset bg-inset px-2.5 py-1.5 font-mono text-[12px] text-ink-strong">
                      "{x.texto_norm}" → {x.producto_nombre ?? <span className="text-ink-3">(producto eliminado)</span>}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => navegar("audio-calidad")}>Abrir el oído</Button>
                <Button variant="outline" size="sm" onClick={() => navegar("catalogo-prueba")}>Abrir catálogo de prueba</Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// «Bitácora» (C-8, tramo 07 de huayruro-orden-y-mapa): lo que el sistema anota con nombre y fecha —vender,
// anular, recibir mercadería, marcar el Mapa—. Solo el Dueño (`GET /audit` es soloSuperAdmin). Carga al
// tocar el botón: son las últimas 100 anotaciones de todo el negocio.
type Evento = { id: string; sucursal: string | null; usuario: string | null; accion: string; fecha_hora: string };
const ACCION: Record<string, string> = {
  venta_registrada: "registró una venta",
  venta_anulada: "anuló una venta",
  recepcion_registrada: "registró una recepción",
  mapa_marcado: "marcó un paso del Mapa",
  mapa_desmarcado: "desmarcó un paso del Mapa",
};
function Bitacora() {
  const [abierta, setAbierta] = useState(false);
  const eventos = useApi<{ eventos: Evento[] }>(abierta ? "/audit" : null, [abierta]);
  const lista = eventos.data?.eventos ?? [];
  return (
    <Card className="col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <SectionLabel>Bitácora</SectionLabel>
          <p className="mt-1 text-[12.5px] leading-relaxed text-ink-2">Quién hizo qué y cuándo: ventas, anulaciones, recepciones y marcas del Mapa. Solo la ves tú.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setAbierta((v) => !v)} className="max-md:min-h-[44px]">
          {abierta ? "Ocultar la bitácora" : "Ver la bitácora"}
        </Button>
      </div>
      {abierta &&
        (eventos.cargando || (!eventos.data && !eventos.error) ? (
          <p className="mt-2 text-[12.5px] text-ink-3">Cargando…</p>
        ) : eventos.error ? (
          <p className="mt-2 text-[12.5px] text-accent-ink">{eventos.error}</p>
        ) : lista.length === 0 ? (
          <p className="mt-2 text-[12.5px] text-ink-3">Todavía no hay nada anotado.</p>
        ) : (
          <ul className="mt-2 max-h-80 overflow-y-auto divide-y divide-line-row">
            {lista.map((e) => (
              <li key={e.id} className="flex items-baseline justify-between gap-3 py-1.5">
                <span className="min-w-0 text-[12.5px] text-ink">
                  <strong className="font-semibold">{e.usuario ?? "El sistema"}</strong> {ACCION[e.accion] ?? e.accion}
                  {e.sucursal ? <span className="text-ink-2"> · {e.sucursal}</span> : null}
                </span>
                <span className="shrink-0 text-[12px] text-ink-2">{fechaCorta(e.fecha_hora)}</span>
              </li>
            ))}
          </ul>
        ))}
    </Card>
  );
}
